import React, { useState, useEffect, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { GoogleMap, useJsApiLoader, Polygon, Polyline, Marker, Autocomplete } from '@react-google-maps/api';
import 'react-toastify/dist/ReactToastify.css';
import { obtenerSectores, guardarSector, eliminarSector } from '../../services/ConfiguracionServicio';
import { obtenerDisponibilidad, guardarTipoZonas } from '../../services/configTiendaApi';
import config from '../../config/appConfig';
import MapaErrorBoundary from '../Generales/MapaErrorBoundary';

// 🚩 Mismo id ('google-map-script') que Pedidos.js y TPV.js, así que TIENE
// que tener el mismo array de libraries en los tres — si no, react-google-maps
// revienta con "Loader must not be called again con distintas options" al
// navegar entre pantallas sin recargar la página (SPA routing).
// 🚩 'drawing' se sacó: Google eliminó DrawingManager de la API (ago-2025
// deprecado, may-2026 removido). El dibujo de la geocerca ahora es manual
// (clics en el mapa), así que ya no se necesita esa librería.
const libraries = ['places', 'geometry'];
const mapContainerStyle = { width: '100%', height: '400px' };
// Ultimo recurso: si no se puede ubicar el local, el mapa abre en Santiago.
const center = { lat: -33.4489, lng: -70.6693 };

// Direccion del local, para abrir el mapa donde el negocio realmente esta.
// Viene del objeto que ya se guarda al elegir sucursal (App.js), asi que no
// hay que pedirsela a nadie. Sin esto el mapa abria siempre en Santiago y un
// cliente de Concepcion tenia que arrastrar medio pais a mano.
const direccionDelLocal = () => {
    try {
        const s = JSON.parse(localStorage.getItem('sucursal')) || {};
        return (s.direccion || '').trim();
    } catch (e) {
        return '';
    }
};

// --- HELPER DE FORMATO CLP ---
const formatPrecio = (valor) => {
    return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0
    }).format(valor || 0);
};


// --- COMPONENTE: MAPA DE DIBUJO ---
// 🚩 Dibujo manual (clic a clic) en vez de DrawingManager: Google eliminó esa
// función de la API (deprecada ago-2025, removida may-2026). Esto solo usa
// el mapa base + Polygon/Polyline/Marker, que siguen soportados.
const MapaZona = ({ coordenadas, onPolygonComplete, isEditable }) => {
    const [puntos, setPuntos] = useState(coordenadas && coordenadas.length > 0 ? coordenadas : []);
    const [cerrado, setCerrado] = useState(!!(coordenadas && coordenadas.length > 0));
    const [mapRef, setMapRef] = useState(null);
    const [autocomplete, setAutocomplete] = useState(null);

    useEffect(() => {
        setPuntos(coordenadas && coordenadas.length > 0 ? coordenadas : []);
        setCerrado(!!(coordenadas && coordenadas.length > 0));
    }, [coordenadas]);

    const handleMapClick = (e) => {
        if (!isEditable || cerrado) return;
        const nuevoPunto = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        setPuntos(prev => [...prev, nuevoPunto]);
    };

    const moverPunto = (index, e) => {
        const actualizado = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        setPuntos(prev => prev.map((p, i) => (i === index ? actualizado : p)));
    };

    const deshacerUltimoPunto = () => setPuntos(prev => prev.slice(0, -1));

    const reiniciarDibujo = () => {
        setPuntos([]);
        setCerrado(false);
        onPolygonComplete(null);
    };

    const finalizarZona = () => {
        if (puntos.length < 3) return;
        setCerrado(true);
        onPolygonComplete(puntos);
    };

    const alBuscarDireccion = () => {
        if (!autocomplete) return;
        const place = autocomplete.getPlace();
        if (!place.geometry || !mapRef) return;
        mapRef.panTo(place.geometry.location);
        mapRef.setZoom(15);
    };

    // 🚩 Zona nueva: abrir el mapa sobre el local, no sobre Santiago.
    // Solo corre cuando NO hay puntos todavia; si la zona ya esta dibujada el
    // mapa arranca sobre ella y mover la camara seria molesto. Si el geocoder
    // falla o la direccion esta cargada como "al lado del super", no se hace
    // nada y queda el comportamiento de siempre.
    useEffect(() => {
        if (!mapRef || puntos.length > 0) return;
        const direccion = direccionDelLocal();
        if (!direccion) return;
        if (!window.google || !window.google.maps || !window.google.maps.Geocoder) return;

        let vigente = true;
        new window.google.maps.Geocoder().geocode(
            { address: direccion, componentRestrictions: { country: 'cl' } },
            (resultados, estado) => {
                if (!vigente) return;
                if (estado !== 'OK' || !resultados || !resultados[0]) return;
                mapRef.panTo(resultados[0].geometry.location);
                mapRef.setZoom(14);
            }
        );
        return () => { vigente = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapRef]);

    // Línea punteada que muestra dónde se cerraría el polígono (último punto → primero)
    const previewCierre = !cerrado && puntos.length >= 2 ? [puntos[puntos.length - 1], puntos[0]] : null;

    return (
        <div>
            {isEditable && (
                <Autocomplete onLoad={setAutocomplete} onPlaceChanged={alBuscarDireccion} options={{ componentRestrictions: { country: 'cl' } }}>
                    <input type="text" className="form-control mb-2" placeholder="Buscar dirección para ubicarte en el mapa..." />
                </Autocomplete>
            )}
            <GoogleMap
                mapContainerStyle={mapContainerStyle}
                zoom={12}
                // 🚩 center es solo el punto de partida: no sigue reactivamente a
                // "puntos" en cada clic, si no el mapa se re-centraba y "saltaba"
                // cada vez que marcabas un punto. Para moverte, usa el buscador o
                // arrastra el mapa; panTo() del buscador mueve la vista sin re-render.
                center={coordenadas && coordenadas.length > 0 ? coordenadas[0] : center}
                onClick={handleMapClick}
                onLoad={setMapRef}
                options={{ streetViewControl: false, mapTypeControl: false }}
            >
                {puntos.length > 0 && cerrado && (
                    <Polygon
                        paths={puntos}
                        options={{ fillColor: '#007bff', fillOpacity: 0.4, strokeColor: '#0056b3', strokeWeight: 3 }}
                    />
                )}
                {puntos.length > 0 && !cerrado && (
                    <Polyline
                        path={puntos}
                        options={{ strokeColor: '#0056b3', strokeWeight: 3 }}
                    />
                )}
                {previewCierre && (
                    <Polyline
                        path={previewCierre}
                        options={{ strokeColor: '#0056b3', strokeWeight: 2, strokeOpacity: 0, icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.7, scale: 3 }, offset: '0', repeat: '12px' }] }}
                    />
                )}
                {isEditable && !cerrado && puntos.map((p, i) => (
                    <Marker
                        key={i}
                        position={p}
                        draggable
                        label={String(i + 1)}
                        onDragEnd={(e) => moverPunto(i, e)}
                    />
                ))}
            </GoogleMap>
            {isEditable && (
                <div className="d-flex justify-content-between align-items-center mt-2">
                    <small className="text-muted">
                        {cerrado ? 'Zona cerrada.' : `${puntos.length} punto(s) marcado(s). Mínimo 3 para cerrar. Puedes arrastrar los pines para ajustar.`}
                    </small>
                    <div className="btn-group btn-group-sm">
                        {!cerrado && (
                            <>
                                <button type="button" className="btn btn-outline-secondary" disabled={puntos.length === 0} onClick={deshacerUltimoPunto}>Deshacer punto</button>
                                <button type="button" className="btn btn-primary" disabled={puntos.length < 3} onClick={finalizarZona}>Cerrar zona</button>
                            </>
                        )}
                        <button type="button" className="btn btn-outline-danger" disabled={puntos.length === 0} onClick={reiniciarDibujo}>Reiniciar</button>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- COMPONENTE: MODAL DE FORMULARIO ---
export const SectorFormModal = ({ isVisible, onClose, onSave, sectorToEdit, tipoZonas = 'geocerca' }) => {
    const [sector, setSector] = useState({
        nombre_sector: '', valor_entrega: 0, monto_minimo_pedido: 0,
        tiempo_estimado: '30-45 min', activo: 1, poligono: null
    });
    // 🚩 Tipo de zona: 'geocerca' (dibujada en el mapa, se detecta sola según
    // la dirección del cliente) o 'fija' (solo nombre + valores, el cliente
    // la elige manualmente porque no hay polígono que la detecte).
    const [isLoading, setIsLoading] = useState(false);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: config.google_api,
        libraries: libraries
    });

    useEffect(() => {
        if (isVisible) {
            const base = sectorToEdit || {
                nombre_sector: '', valor_entrega: 0, monto_minimo_pedido: 0,
                tiempo_estimado: '30-45 min', activo: 1, poligono: null
            };
            setSector(base);
        }
    }, [sectorToEdit, isVisible]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!sector.nombre_sector) return toast.warning("Nombre obligatorio");
        if (tipoZonas === 'geocerca' && !sector.poligono) return toast.warning("Debes dibujar la zona en el mapa");

        // Zonas "por nombre": se guardan sin polígono, sin importar si venía uno dibujado antes.
        const sectorAGuardar = tipoZonas === 'nombre' ? { ...sector, poligono: null } : sector;

        setIsLoading(true);
        try {
            await guardarSector(sectorAGuardar);
            onSave();
        } catch (err) { toast.error("Error al guardar"); }
        finally { setIsLoading(false); }
    };

    if (!isVisible) return null;

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', zIndex: 1050 }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content border-0 shadow-lg rounded-xl">
                    <div className="modal-header border-0 pt-4 px-4">
                        <h5 className="font-weight-bold m-0">{sectorToEdit ? 'Editar Zona de Reparto' : 'Nueva Zona de Reparto'}</h5>
                        <button className="close" onClick={onClose}>&times;</button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body p-4">
                            <div className="alert alert-light border small mb-3">
                                {tipoZonas === 'geocerca'
                                    ? <><i className="fas fa-draw-polygon mr-1"></i> Zona con geocerca: se detecta sola cuando la dirección del cliente cae dentro del área dibujada.</>
                                    : <><i className="fas fa-map-marker-alt mr-1"></i> Zona por nombre: el cliente la elige manualmente de una lista.</>}
                                {' '}<span className="text-muted">(cambia el enfoque para todas las zonas en "Zonas de despacho" → "Por sectores").</span>
                            </div>
                            <div className="row">
                                <div className={tipoZonas === 'geocerca' ? 'col-md-5' : 'col-md-12'}>
                                    <div className="form-group mb-3">
                                        <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Nombre del Sector</label>
                                        <input type="text" className="form-control border-light bg-light" value={sector.nombre_sector} onChange={(e) => setSector({ ...sector, nombre_sector: e.target.value })} placeholder="Ej: Providencia Norte" />
                                    </div>
                                    <div className="form-group mb-3">
                                        <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Costo de Envío</label>
                                        <div className="input-group">
                                            <div className="input-group-prepend"><span className="input-group-text">$</span></div>
                                            <input type="number" className="form-control border-light bg-light" value={sector.valor_entrega} onChange={(e) => setSector({ ...sector, valor_entrega: e.target.value })} />
                                        </div>
                                        <small className="text-primary font-weight-bold">{formatPrecio(sector.valor_entrega)}</small>
                                    </div>
                                    <div className="form-group mb-3">
                                        <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Mínimo de Compra</label>
                                        <div className="input-group">
                                            <div className="input-group-prepend"><span className="input-group-text">$</span></div>
                                            <input type="number" className="form-control border-light bg-light" value={sector.monto_minimo_pedido} onChange={(e) => setSector({ ...sector, monto_minimo_pedido: e.target.value })} />
                                        </div>
                                        <small className="text-muted">{formatPrecio(sector.monto_minimo_pedido)}</small>
                                    </div>
                                    <div className="form-group mb-3">
                                        <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Tiempo de Entrega</label>
                                        <input type="text" className="form-control border-light bg-light" value={sector.tiempo_estimado} onChange={(e) => setSector({ ...sector, tiempo_estimado: e.target.value })} />
                                    </div>
                                </div>
                                {tipoZonas === 'geocerca' && (
                                    <div className="col-md-7">
                                        <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block text-center">Dibuja la zona en el mapa</label>
                                        <div className="rounded border overflow-hidden shadow-sm">
                                            {isLoaded ? (
                                                <MapaErrorBoundary
                                                    key={sector.poligono ? 'con-datos' : 'vacio'}
                                                    fallback={<div className="alert alert-warning text-center small mb-0" style={{ borderRadius: '10px' }}>⚠️ No se pudo cargar el mapa. Puedes seguir usando "Zona fija" mientras tanto.</div>}
                                                >
                                                    <MapaZona coordenadas={sector.poligono} onPolygonComplete={(path) => setSector({ ...sector, poligono: path })} isEditable={true} />
                                                </MapaErrorBoundary>
                                            ) : <div className="p-5 text-center">Cargando Mapa...</div>}
                                        </div>
                                        <p className="small text-muted mt-2 text-center">Haz clic en el mapa para empezar a marcar puntos.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer border-0 p-4 pt-0">
                            <button type="button" className="btn btn-link text-muted" onClick={onClose}>Cancelar</button>
                            <button type="submit" className="btn btn-primary btn-lg rounded-pill px-5 shadow" disabled={isLoading}>{isLoading ? 'Guardando...' : 'Guardar Zona'}</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// --- COMPONENTE PRINCIPAL ---
const AdministradorDespachos = ({ embedded, tipoZonas: tipoZonasProp }) => {
    const [sectores, setSectores] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sectorToEdit, setSectorToEdit] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    // 🚩 Si no llega por prop (uso standalone en /admin/despachos/), se busca
    // sola. Si llega por prop (embebida en Tienda, que ya la tiene cargada),
    // se usa esa — evita pedirla dos veces.
    const [tipoZonasPropia, setTipoZonasPropia] = useState('geocerca');
    const [guardandoTipo, setGuardandoTipo] = useState(false);
    const tipoZonas = tipoZonasProp || tipoZonasPropia;

    // 🚩 El enfoque de zonas se elegia solo en el Configurador de Pedidos Web,
    // dos pantallas mas alla. Quien entraba aca veia el mapa y asumia que la
    // geocerca era la unica forma de armar una zona. Ahora se cambia desde el
    // mismo lugar donde se administran.
    const cambiarTipoZonas = async (tipo) => {
        if (tipo === tipoZonas || guardandoTipo) return;

        // Pasar a "por nombre" borra los poligonos de TODAS las zonas del
        // negocio (lo hace el backend, de una) y no hay como recuperarlos.
        if (tipo === 'nombre') {
            const conGeocerca = sectores.filter(s => s.poligono && s.poligono.length > 0).length;
            if (conGeocerca > 0) {
                const ok = window.confirm(
                    `Tenés ${conGeocerca} zona${conGeocerca > 1 ? 's' : ''} con geocerca dibujada.\n\n` +
                    `Al cambiar a "Por nombre" se borran esos dibujos y no se pueden recuperar: ` +
                    `si después volvés a geocerca, hay que dibujarlas de nuevo.\n\n¿Continuar?`
                );
                if (!ok) return;
            }
        }

        setGuardandoTipo(true);
        try {
            await guardarTipoZonas(tipo);
            setTipoZonasPropia(tipo);
            await cargarSectores();
            toast.success(tipo === 'geocerca' ? 'Zonas por geocerca' : 'Zonas por nombre');
        } catch (err) {
            toast.error('No se pudo cambiar el enfoque de las zonas');
        } finally {
            setGuardandoTipo(false);
        }
    };

    useEffect(() => {
        cargarSectores();
        if (!tipoZonasProp) {
            obtenerDisponibilidad().then(d => {
                if (d && d.tipoZonas) setTipoZonasPropia(d.tipoZonas);
            }).catch(() => {});
        }
    }, []);

    const cargarSectores = async () => {
        setIsLoading(true);
        try {
            const data = await obtenerSectores();
            const procesados = data.map(s => {
                let poli = s.poligono;
                if (typeof poli === 'string' && poli !== "") {
                    try { poli = JSON.parse(poli); } catch (e) { poli = null; }
                }
                return { ...s, poligono: poli };
            });
            setSectores(procesados || []);
        } catch (err) { toast.error("Error de conexión"); }
        finally { setIsLoading(false); }
    };

    const handleDelete = async (id, nombre) => {
        if (!window.confirm(`¿Eliminar la zona "${nombre}"?`)) return;
        try {
            await eliminarSector(id);
            toast.success("Zona eliminada");
            cargarSectores();
        } catch (err) { toast.error("No se pudo eliminar"); }
    };

    const handleSave = () => {
        setIsModalOpen(false);
        setSectorToEdit(null);
        toast.success("¡Datos actualizados!");
        cargarSectores();
    };

    const filtered = sectores.filter(s => s.nombre_sector.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <div className="bg-light min-vh-100 py-5 px-3">
            <ToastContainer position="bottom-center" autoClose={2500} hideProgressBar />
            <div className="container" style={{ maxWidth: '1100px' }}>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-5">
                    <div>
                        <h1 className="h2 font-weight-bold text-dark mb-1">Zonas de Despacho</h1>
                        <p className="text-muted">Define las áreas de entrega y sus costos en CLP.</p>
                    </div>
                    <button className="btn btn-primary btn-lg rounded-pill shadow-lg px-4" onClick={() => { setSectorToEdit(null); setIsModalOpen(true); }}>
                        <i className={`fas ${tipoZonas === 'geocerca' ? 'fa-draw-polygon' : 'fa-plus'} mr-2`}></i> Nueva Zona
                    </button>
                </div>

                {/* 🚩 Enfoque de las zonas. Solo en uso standalone: cuando esta
                    embebida en el Configurador de Pedidos Web, ese toggle ya
                    existe alla y tenerlo dos veces confunde. */}
                {!tipoZonasProp && (
                    <div className="card border-0 shadow-sm rounded-xl mb-4">
                        <div className="card-body p-4">
                            <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Cómo querés definir las zonas</label>
                            <div className="btn-group w-100 mb-2" role="group">
                                <button type="button" disabled={guardandoTipo}
                                    className={`btn ${tipoZonas === 'geocerca' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => cambiarTipoZonas('geocerca')}>
                                    <i className="fas fa-draw-polygon mr-1"></i> Geocerca (mapa)
                                </button>
                                <button type="button" disabled={guardandoTipo}
                                    className={`btn ${tipoZonas === 'nombre' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => cambiarTipoZonas('nombre')}>
                                    <i className="fas fa-list mr-1"></i> Por nombre (lista)
                                </button>
                            </div>
                            <p className="text-muted small mb-0">
                                {tipoZonas === 'geocerca'
                                    ? 'Dibujás el área de cada zona en el mapa y se detecta sola según la dirección del cliente.'
                                    : 'Cada zona es solo un nombre y un costo. El cliente la elige de una lista, sin mapa.'}
                                {' '}Aplica a todas las zonas de este negocio.
                            </p>
                        </div>
                    </div>
                )}

                <div className="card border-0 shadow-sm rounded-xl overflow-hidden">
                    <div className="card-header bg-white border-0 pt-4 px-4 pb-2">
                        <div className="input-group bg-light rounded-pill px-3 border shadow-inner">
                            <div className="input-group-prepend border-0 bg-transparent">
                                <span className="input-group-text bg-transparent border-0 text-muted"><i className="fas fa-search"></i></span>
                            </div>
                            <input type="text" className="form-control bg-transparent border-0" placeholder="Buscar por nombre de sector..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        </div>
                    </div>

                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="bg-light text-muted small text-uppercase">
                                    <tr>
                                        <th className="border-0 px-4 py-3">Sector</th>
                                        <th className="border-0 py-3">Costo Envío</th>
                                        <th className="border-0 py-3">Mínimo Pedido</th>
                                        <th className="border-0 py-3 text-center">Estado</th>
                                        <th className="border-0 px-4 py-3 text-right">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr><td colSpan="5" className="text-center py-5"><div className="spinner-border text-primary"></div></td></tr>
                                    ) : filtered.map(s => (
                                        <tr key={s.id}>
                                            <td className="px-4 py-3">
                                                <div className="font-weight-bold text-dark">
                                                    {s.nombre_sector}{' '}
                                                    <span className={`badge badge-pill ${s.poligono && s.poligono.length > 0 ? 'badge-soft-success' : 'badge-soft-secondary'}`} style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}>
                                                        {s.poligono && s.poligono.length > 0 ? 'Geocerca' : 'Fija'}
                                                    </span>
                                                </div>
                                                <small className="text-muted"><i className="far fa-clock mr-1"></i> {s.tiempo_estimado}</small>
                                            </td>
                                            <td className="py-3 font-weight-bold text-primary">{formatPrecio(s.valor_entrega)}</td>
                                            <td className="py-3 text-dark">{formatPrecio(s.monto_minimo_pedido)}</td>
                                            <td className="py-3 text-center">
                                                <span className={`badge px-3 py-2 rounded-pill ${s.activo ? 'badge-soft-success' : 'badge-soft-secondary'}`}>
                                                    {s.activo ? 'Habilitado' : 'Deshabilitado'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <button className="btn btn-icon btn-light rounded-circle mr-2 text-primary" onClick={() => { setSectorToEdit(s); setIsModalOpen(true); }} title="Editar polígono y datos"><i className="fas fa-pen"></i></button>
                                                <button className="btn btn-icon btn-light rounded-circle text-danger" onClick={() => handleDelete(s.id, s.nombre_sector)} title="Eliminar zona"><i className="fas fa-trash"></i></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {!isLoading && filtered.length === 0 && <div className="text-center py-5 text-muted">No hay zonas configuradas.</div>}
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .rounded-xl { border-radius: 1.25rem !important; }
                .badge-soft-success { background-color: #e6fcf5; color: #087f5b; }
                .badge-soft-secondary { background-color: #f1f3f5; color: #495057; }
                .btn-icon { width: 36px; height: 36px; border: none; display: inline-flex; align-items: center; justify-content: center; transition: 0.2s; }
                .btn-icon:hover { background-color: #e9ecef; transform: scale(1.1); }
                .shadow-inner { box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05); }
            `}</style>

            <SectorFormModal isVisible={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSave} sectorToEdit={sectorToEdit} tipoZonas={tipoZonas} />
        </div>
    );
};

export default AdministradorDespachos;